import { useEffect, useRef } from 'react';

// Scales the Turnstile wrapper using CSS transform so the shadow DOM iframe fills the container width
// baseWidth/baseHeight approximate Turnstile's normal size
export default function useTurnstileScale(containerRef, options = {}, deps = []) {
  const { baseWidth = 300, baseHeight = 65, maxScale = 3 } = options;

  useEffect(() => {
    // Refs to hold state across closures
    const lastWidthRef = { current: 0 };
    const lastScaleRef = { current: 0 };
    const rafIdRef = { current: 0 };
    const cleanupRef = { current: null };
    let pending = false;

    const attemptInit = () => {
      const el = containerRef?.current;
      if (!el) {
        // Ref not ready yet, retry on next frame
        rafIdRef.current = requestAnimationFrame(attemptInit);
        return;
      }

      // If we have an element but it doesn't have style (e.g. component instance), 
      // check if it has a DOM node via findDOMNode or just wait?
      // Assuming it's a DOM node or has style property.
      if (!el.style) {
        // If it's not a DOM node yet, retry
        rafIdRef.current = requestAnimationFrame(attemptInit);
        return;
      }

      const isRtl = () => (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
      const applyOrigin = () => {
        el.style.transformOrigin = isRtl() ? 'top right' : 'top left';
      };
      applyOrigin();
      el.style.width = '100%';

      const applyScale = () => {
        pending = false;
        const containerWidth = el.clientWidth || el.offsetWidth || 0;
        
        // If container width is 0, we might be hidden or not laid out yet.
        if (!containerWidth) {
           // We can retry later via resize observer
           return; 
        }
        if (!baseWidth) return;

        const scale = Math.min(Math.max(containerWidth / baseWidth, 0.1), maxScale);
        
        // Skip if width/scale are effectively unchanged
        if (
          Math.abs(containerWidth - lastWidthRef.current) < 1 &&
          Math.abs(scale - lastScaleRef.current) < 0.005
        ) {
          return;
        }
        lastWidthRef.current = containerWidth;
        lastScaleRef.current = scale;
        el.style.transform = `scale(${scale})`;
        el.style.height = `${Math.ceil(baseHeight * scale)}px`;
      };

      const schedule = () => {
        if (pending) return;
        pending = true;
        rafIdRef.current = window.requestAnimationFrame(applyScale);
      };

      // Initial apply
      applyScale();

      const resizeObserver = new ResizeObserver(() => schedule());
      resizeObserver.observe(el);

      window.addEventListener('resize', schedule, { passive: true });

      // Observe direction changes (LTR/RTL) on <html dir="...">
      const mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes') {
            if (m.attributeName === 'dir') {
              applyOrigin();
              schedule();
              break;
            }
            if (m.attributeName === 'data-theme') {
              // Theme toggle can affect layout; rescale
              schedule();
              break;
            }
          }
        }
      });
      try {
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['dir', 'data-theme'] });
      } catch (_) {}

      // Store cleanup function
      cleanupRef.current = () => {
        window.removeEventListener('resize', schedule);
        try { resizeObserver.disconnect(); } catch (_) {}
        try { mo.disconnect(); } catch (_) {}
      };
    };

    attemptInit();

    return () => {
      if (cleanupRef.current) cleanupRef.current();
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, baseWidth, baseHeight, maxScale, ...deps]);
}
